import { TinyColor } from '@ctrl/tinycolor';
import { css } from 'aphrodite';
import { ReactNode, useState } from 'react';
import { Button } from '../../../components-v2/Button';
import { MultiColorPillProps } from '../../../components-v2/Pill';
import { LoadingSpinner } from '../../LoadingSpinner';
import { SimpleTooltip } from '../../Tooltip';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { PlusIcon2 } from '../../svgs/icons/PlusIcon';
import { iconTileColors, styleSheet } from './styles';

export type DataBoardIconColor = 'green' | 'pink' | 'blue';

export interface DataBoardDetail {
	label: string;
	value: ReactNode;
}

export interface DataBoardRow {
	id: string;
	name: ReactNode;
	status?: ReactNode;
	statusTitle?: string;
	fields: ReactNode[];
	value?: ReactNode;
	details?: DataBoardDetail[];
	actions?: ReactNode;
}

interface DataBoardTableProps {
	icon: ReactNode;
	iconColor: DataBoardIconColor;
	title: string;
	summary?: ReactNode;
	addItemName?: string;
	onAdd?: () => void;
	addDisabledReason?: string;
	nameLabel: string;
	statusLabel?: string;
	fieldLabels: string[];
	valueLabel?: string;
	rows: DataBoardRow[];
	isLoading?: boolean;
	emptyText?: ReactNode;
	children?: ReactNode;
}

const DETAILS_COLUMN_WIDTH = 32;
const ACTIONS_COLUMN_WIDTH = 32;

export { styleSheet as dataBoardStyles };

// Campaigns and lines of business store a free-form hex, while Pill only takes palette names, so snap to the nearest hue.
export const pillColorFromHex = (hex: string): MultiColorPillProps['color'] => {
	const { h, s } = new TinyColor(hex).toHsl();
	if (s < 0.15) return 'gray';
	if (h < 15 || h >= 345) return 'red';
	if (h < 45) return 'orange';
	if (h < 70) return 'yellow';
	if (h < 160) return 'green';
	if (h < 200) return 'teal';
	if (h < 255) return 'blue';
	if (h < 290) return 'purple';
	return 'pink';
};

export const DataBoardTable = ({
	icon,
	iconColor,
	title,
	summary,
	addItemName,
	onAdd,
	addDisabledReason,
	nameLabel,
	statusLabel,
	fieldLabels,
	valueLabel,
	rows,
	isLoading,
	emptyText,
	children,
}: DataBoardTableProps) => {
	const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());

	const expandableIds = rows.filter(row => !!row.details?.length).map(row => row.id);
	const hasActions = rows.some(row => !!row.actions);
	const allOpen = expandableIds.length > 0 && expandableIds.every(id => openIds.has(id));
	const trailingWidth = (expandableIds.length ? DETAILS_COLUMN_WIDTH : 0) + (hasActions ? ACTIONS_COLUMN_WIDTH : 0);

	const gridTemplateColumns = [
		'minmax(160px, 1.4fr)',
		statusLabel ? '120px' : null,
		fieldLabels.length ? `repeat(${fieldLabels.length}, minmax(110px, 1fr))` : null,
		valueLabel ? '100px' : null,
		trailingWidth ? `${trailingWidth}px` : null,
	]
		.filter(Boolean)
		.join(' ');

	const toggleRow = (id: string) => {
		setOpenIds(current => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const toggleAll = () => {
		setOpenIds(allOpen ? new Set() : new Set(expandableIds));
	};

	const renderBody = () => {
		if (isLoading) {
			return <LoadingSpinner />;
		}

		if (!rows.length) {
			return emptyText ? <p className={css(styleSheet.emptyText)}>{emptyText}</p> : null;
		}

		return (
			<div className={css(styleSheet.tableScroller)}>
				<div role='table' aria-label={title} className={css(styleSheet.table)}>
					<div role='row' className={css(styleSheet.gridRow, styleSheet.headerRow)} style={{ gridTemplateColumns }}>
						<span role='columnheader'>{nameLabel}</span>
						{statusLabel ? <span role='columnheader'>{statusLabel}</span> : null}
						{fieldLabels.map(label => (
							<span key={label} role='columnheader'>
								{label}
							</span>
						))}
						{valueLabel ? (
							<span role='columnheader' className={css(styleSheet.alignEnd)}>
								{valueLabel}
							</span>
						) : null}
						{trailingWidth ? <span role='columnheader' /> : null}
					</div>

					{rows.map(row => {
						const hasDetails = !!row.details?.length;
						const isOpen = hasDetails && openIds.has(row.id);
						const detailsId = `data-board-details-${row.id}`;
						return (
							<div key={row.id} role='rowgroup' className={css(styleSheet.rowGroup)}>
								<div role='row' className={css(styleSheet.gridRow, styleSheet.bodyRow)} style={{ gridTemplateColumns }}>
									<span role='cell' className={css(styleSheet.nameCell)}>
										{row.name}
									</span>
									{statusLabel ? (
										<span role='cell' className={css(styleSheet.cell, styleSheet.statusCell)}>
											{row.statusTitle ? (
												<SimpleTooltip title={row.statusTitle}>
													<span className={css(styleSheet.statusCell)}>{row.status}</span>
												</SimpleTooltip>
											) : (
												row.status
											)}
										</span>
									) : null}
									{row.fields.map((field, index) => (
										<span key={fieldLabels[index] ?? index} role='cell' className={css(styleSheet.cell)}>
											{field}
										</span>
									))}
									{valueLabel ? (
										<span role='cell' className={css(styleSheet.cell, styleSheet.alignEnd)}>
											{row.value}
										</span>
									) : null}
									{trailingWidth ? (
										<span role='cell' className={css(styleSheet.trailingCell)}>
											{hasDetails ? (
												<Button
													variant='ghost'
													size='icon'
													aria-label={isOpen ? 'Hide details' : 'Show details'}
													aria-expanded={isOpen}
													aria-controls={detailsId}
													onClick={() => toggleRow(row.id)}
													styles={[styleSheet.chevronButton]}
												>
													<ChevronDown className={css(styleSheet.chevron, isOpen && styleSheet.chevronOpen)} />
												</Button>
											) : null}
											{row.actions}
										</span>
									) : null}
								</div>
								{isOpen ? (
									<div role='row' id={detailsId} className={css(styleSheet.detailsRow)}>
										<div role='cell' className={css(styleSheet.detailsList)}>
											{row.details?.map(detail => (
												<div key={detail.label} className={css(styleSheet.detail)}>
													<span className={css(styleSheet.detailLabel)}>{detail.label}</span>
													<span className={css(styleSheet.detailValue)}>{detail.value}</span>
												</div>
											))}
										</div>
									</div>
								) : null}
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const addButton =
		addItemName && onAdd ? (
			<Button
				size='sm'
				onClick={addDisabledReason ? undefined : onAdd}
				aria-disabled={addDisabledReason ? true : undefined}
				styles={addDisabledReason ? [styleSheet.addButtonDisabled] : undefined}
			>
				<PlusIcon2 />
				<span>
					Add<span className={css(styleSheet.addItemName)}> {addItemName}</span>
				</span>
			</Button>
		) : null;

	return (
		<section className={css(styleSheet.board)}>
			<header className={css(styleSheet.header)}>
				<div className={css(styleSheet.iconTile, iconTileColors[iconColor])}>{icon}</div>
				<div className={css(styleSheet.heading)}>
					<h6 className={css(styleSheet.title)}>{title}</h6>
					{summary ? <div className={css(styleSheet.summary)}>{summary}</div> : null}
				</div>
				<div className={css(styleSheet.actionsContainer)}>
					{addButton && addDisabledReason ? (
						<SimpleTooltip title={addDisabledReason}>{addButton}</SimpleTooltip>
					) : (
						addButton
					)}
					{expandableIds.length && !isLoading ? (
						<Button variant='link' onClick={toggleAll} styles={[styleSheet.toggleAll]}>
							{allOpen ? 'Hide all' : 'Show all'}
							<ChevronDown className={css(styleSheet.chevron, allOpen && styleSheet.chevronOpen)} />
						</Button>
					) : null}
				</div>
			</header>

			{renderBody()}
			{children}
		</section>
	);
};
